import React from 'react';
import {
  Brain, Heart, Shield, Target, Zap, Star, Activity, Users, Clock, TrendingUp,
  Lightbulb, CheckCircle2, Phone, BookOpen, AlertTriangle, Stethoscope, XCircle,
  ArrowRight, ArrowDown,
} from 'lucide-react';

const iconMap = {
  Brain, Heart, Shield, Target, Zap, Star, Activity, Users, Clock, TrendingUp,
  Lightbulb, CheckCircle2, Phone, BookOpen, AlertTriangle, Stethoscope,
};
const Ico = ({ name, className }) => {
  const C = iconMap[name] || Star;
  return <C className={className} />;
};

export function OnePagerPreview({ onePager }) {
  const op = onePager || {};
  const c = op.content || {};
  const cs = op.colorScheme || { primary: '#0D9488', secondary: '#5EEAD4', background: '#F0FDFA', text: '#134E4A', accent: '#14B8A6' };
  const layout = op.layout || 'standard';
  const info = c.infographic || {};

  const Header = () => (
    <div className="flex items-start justify-between gap-3 pb-3 mb-3 border-b-2" style={{ borderColor: cs.primary }}>
      <div className="flex-1 min-w-0">
        <h1 className="text-2xl font-bold leading-tight" style={{ color: cs.primary }}>{c.title || op.title || 'Untitled'}</h1>
        {(c.subtitle || op.subtitle) && <h2 className="text-sm font-medium mt-1" style={{ color: cs.text }}>{c.subtitle || op.subtitle}</h2>}
      </div>
      {op.facilityLogo ? (
        <img src={op.facilityLogo} alt="logo" className="h-12 max-w-[130px] object-contain" />
      ) : op.facilityName ? (
        <div className="text-right text-xs font-semibold" style={{ color: cs.primary }}>{op.facilityName}</div>
      ) : null}
    </div>
  );

  const Footer = () => (
    <div className="mt-auto pt-2 border-t text-[9px] flex justify-between" style={{ borderColor: cs.secondary, color: cs.text }}>
      <span>{op.facilityName || 'NikoSafe Training'}</span>
      <span>{c.versionDate || new Date().toLocaleDateString()}</span>
    </div>
  );

  const Bullets = ({ items, title, icon }) => (
    <div className="mb-3">
      {title && <h3 className="text-sm font-bold mb-1.5 flex items-center gap-1" style={{ color: cs.primary }}>{icon}{title}</h3>}
      <ul className="space-y-1">
        {(items || []).map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-xs" style={{ color: cs.text }}>
            <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: cs.accent }} />
            <span>{typeof p === 'string' ? p : p.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  const Body = () => {
    switch (layout) {
      case 'two-column':
        return (
          <>
            {c.introduction && <p className="text-xs mb-3" style={{ color: cs.text }}>{c.introduction}</p>}
            <div className="grid grid-cols-2 gap-3 flex-1">
              {['leftColumn', 'rightColumn'].map((col, idx) => (
                <div key={col} className="rounded-lg p-3" style={{ backgroundColor: cs.background }}>
                  {(c[col] || '').split('\n').map((line, i) => {
                    const t = line.replace(/^-\s*/, '');
                    if (!t.trim()) return null;
                    return <div key={i} className="text-xs mb-1 flex items-start gap-1.5" style={{ color: cs.text }}><span style={{ color: cs.accent }}>•</span>{t}</div>;
                  })}
                </div>
              ))}
            </div>
            {c.keyPoints && <Bullets items={c.keyPoints} title="Key Takeaways" />}
          </>
        );
      case 'crisis':
        return (
          <>
            {c.emergencyInfo && (
              <div className="rounded-lg p-3 mb-3 border-2" style={{ borderColor: '#DC2626', backgroundColor: '#FEF2F2' }}>
                <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4 text-red-600" /><span className="text-sm font-bold text-red-700">Emergency Information</span></div>
                <p className="text-xs text-red-800">{c.emergencyInfo}</p>
              </div>
            )}
            <Bullets items={c.keyPoints} title="Warning Signs" icon={<AlertTriangle className="w-4 h-4" />} />
            {c.hotlines && (
              <div className="space-y-1.5 mb-3">
                {c.hotlines.map((h, i) => (
                  <div key={i} className="rounded-lg p-2 flex items-center gap-2" style={{ backgroundColor: cs.background }}>
                    <Phone className="w-4 h-4 shrink-0" style={{ color: cs.primary }} />
                    <div><div className="text-xs font-bold" style={{ color: cs.primary }}>{h.name} — {h.number}</div><div className="text-[10px]" style={{ color: cs.text }}>{h.description}</div></div>
                  </div>
                ))}
              </div>
            )}
          </>
        );
      case 'medication': {
        const m = c.medicationInfo || {};
        return (
          <>
            {c.introduction && <p className="text-xs mb-3" style={{ color: cs.text }}>{c.introduction}</p>}
            {m.purpose && <div className="rounded-lg p-2 mb-2" style={{ backgroundColor: cs.background }}><span className="text-xs font-bold" style={{ color: cs.primary }}>Purpose: </span><span className="text-xs" style={{ color: cs.text }}>{m.purpose}</span></div>}
            <div className="grid grid-cols-2 gap-3">
              {m.instructions && <Bullets items={m.instructions} title="Instructions" />}
              {m.sideEffects && <Bullets items={m.sideEffects} title="Side Effects" />}
            </div>
            {m.warnings && (
              <div className="rounded-lg p-2 mb-2 border" style={{ borderColor: '#F59E0B', backgroundColor: '#FFFBEB' }}>
                <h3 className="text-xs font-bold text-amber-700 mb-1">⚠ Warnings</h3>
                {m.warnings.map((w, i) => <div key={i} className="text-[11px] text-amber-800">• {w}</div>)}
              </div>
            )}
          </>
        );
      }
      case 'infographic-stats':
        return (
          <>
            {c.introduction && <p className="text-xs mb-3 text-center" style={{ color: cs.text }}>{c.introduction}</p>}
            <div className="grid grid-cols-2 gap-3 mb-3">
              {(info.stats || []).map((s, i) => (
                <div key={i} className="rounded-xl p-3 text-center" style={{ backgroundColor: cs.background }}>
                  <Ico name={s.icon} className="w-6 h-6 mx-auto mb-1" style={{ color: cs.primary }} />
                  <div className="text-2xl font-extrabold" style={{ color: cs.primary }}>{s.value}</div>
                  <div className="text-xs font-semibold" style={{ color: cs.text }}>{s.label}</div>
                  {s.description && <div className="text-[10px]" style={{ color: cs.text }}>{s.description}</div>}
                </div>
              ))}
            </div>
            {c.keyPoints && <Bullets items={c.keyPoints} title="Key Insights" />}
          </>
        );
      case 'infographic-process':
        return (
          <>
            {c.introduction && <p className="text-xs mb-3" style={{ color: cs.text }}>{c.introduction}</p>}
            <div className="space-y-2 flex-1">
              {(info.processSteps || []).map((s, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shrink-0" style={{ backgroundColor: cs.primary }}>{s.number}</div>
                  <div className="flex-1 rounded-lg p-2" style={{ backgroundColor: cs.background }}>
                    <div className="text-xs font-bold flex items-center gap-1" style={{ color: cs.primary }}><Ico name={s.icon} className="w-3.5 h-3.5" />{s.title}</div>
                    <div className="text-[11px]" style={{ color: cs.text }}>{s.description}</div>
                  </div>
                </div>
              ))}
            </div>
            {c.tips && <Bullets items={c.tips} title="Tips" icon={<Lightbulb className="w-4 h-4" />} />}
          </>
        );
      case 'infographic-comparison': {
        const cmp = info.comparison || {};
        return (
          <>
            {c.introduction && <p className="text-xs mb-3 text-center" style={{ color: cs.text }}>{c.introduction}</p>}
            <div className="grid grid-cols-2 gap-3 flex-1">
              {[['leftTitle', 'leftItems'], ['rightTitle', 'rightItems']].map(([tk, ik], idx) => (
                <div key={idx} className="rounded-lg p-3" style={{ backgroundColor: cs.background }}>
                  <h3 className="text-sm font-bold mb-2 text-center" style={{ color: cs.primary }}>{cmp[tk]}</h3>
                  {(cmp[ik] || []).map((it, i) => <div key={i} className="text-xs mb-1 flex gap-1.5" style={{ color: cs.text }}><CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: cs.accent }} />{it}</div>)}
                </div>
              ))}
            </div>
            {c.keyPoints && <Bullets items={c.keyPoints} title="Key Takeaways" />}
          </>
        );
      }
      case 'infographic-timeline':
        return (
          <>
            {c.introduction && <p className="text-xs mb-3" style={{ color: cs.text }}>{c.introduction}</p>}
            <div className="relative pl-6 flex-1">
              <div className="absolute left-2 top-1 bottom-1 w-0.5" style={{ backgroundColor: cs.secondary }} />
              {(info.timeline || []).map((t, i) => (
                <div key={i} className="relative mb-3">
                  <div className="absolute -left-[18px] w-3 h-3 rounded-full border-2 border-white" style={{ backgroundColor: cs.primary }} />
                  <div className="text-xs font-bold" style={{ color: cs.primary }}>{t.time} — {t.title}</div>
                  <div className="text-[11px]" style={{ color: cs.text }}>{t.description}</div>
                </div>
              ))}
            </div>
            {c.affirmation && <div className="rounded-lg p-2 text-center text-xs font-medium" style={{ backgroundColor: cs.background, color: cs.primary }}>{c.affirmation}</div>}
          </>
        );
      case 'infographic-icons':
        return (
          <>
            {c.introduction && <p className="text-xs mb-3 text-center" style={{ color: cs.text }}>{c.introduction}</p>}
            <div className="grid grid-cols-2 gap-3 mb-3">
              {(info.iconSections || []).map((s, i) => (
                <div key={i} className="rounded-lg p-3 text-center" style={{ backgroundColor: cs.background }}>
                  <div className="w-10 h-10 rounded-full mx-auto mb-1 flex items-center justify-center" style={{ backgroundColor: cs.primary }}><Ico name={s.icon} className="w-5 h-5 text-white" /></div>
                  <div className="text-xs font-bold" style={{ color: cs.primary }}>{s.title}</div>
                  <div className="text-[10px]" style={{ color: cs.text }}>{s.description}</div>
                </div>
              ))}
            </div>
            {c.tips && <Bullets items={c.tips} title="Remember" icon={<Lightbulb className="w-4 h-4" />} />}
          </>
        );
      case 'checklist': {
        const sections = c.checklist?.sections || [];
        return (
          <>
            {c.introduction && <p className="text-xs mb-3" style={{ color: cs.text }}>{c.introduction}</p>}
            <div className="space-y-3 flex-1">
              {sections.map((s, i) => (
                <div key={i} className="checklist-section">
                  <h3 className="text-sm font-bold mb-1.5 pb-1 border-b" style={{ color: cs.primary, borderColor: cs.secondary }}>{s.title}</h3>
                  {(s.items || []).map((it, j) => (
                    <div key={j} className="flex items-start gap-2 text-xs mb-1" style={{ color: cs.text }}>
                      <div className="checklist-checkbox w-3.5 h-3.5 border-2 rounded mt-0.5 shrink-0" style={{ borderColor: it.critical ? '#DC2626' : cs.primary }} />
                      <span className={it.critical ? 'font-semibold' : ''}>{it.text}{it.critical && <span className="text-red-600 ml-1 text-[9px] font-bold">CRITICAL</span>}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            {c.affirmation && <div className="rounded-lg p-2 text-center text-xs font-medium mt-2" style={{ backgroundColor: cs.background, color: cs.primary }}>{c.affirmation}</div>}
          </>
        );
      }
      case 'process-roles': {
        const steps = info.roleProcessSteps || [];
        const roles = info.roleDefinitions || [];
        const roleColor = (r) => roles.find((rd) => rd.abbreviation === r)?.color || cs.primary;
        return (
          <>
            {roles.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {roles.map((r, i) => <span key={i} className="text-[10px] px-2 py-0.5 rounded-full text-white font-semibold" style={{ backgroundColor: r.color }}>{r.abbreviation} = {r.fullName}</span>)}
              </div>
            )}
            <div className="space-y-1.5 flex-1">
              {steps.map((s, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg p-2" style={{ backgroundColor: cs.background }}>
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: cs.primary }}>{s.number}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-bold" style={{ color: cs.text }}>{s.title}</span>
                      {(s.roles || []).map((r, j) => <span key={j} className="role-badge text-[9px] px-1.5 py-0.5 rounded-full text-white font-semibold" style={{ backgroundColor: roleColor(r) }}>{r}</span>)}
                      {s.critical && <span className="text-[9px] text-red-600 font-bold">CRITICAL</span>}
                    </div>
                    <div className="text-[11px]" style={{ color: cs.text }}>{s.description}</div>
                    {s.timeframe && <div className="text-[9px] flex items-center gap-0.5" style={{ color: cs.accent }}><Clock className="w-2.5 h-2.5" />{s.timeframe}</div>}
                  </div>
                </div>
              ))}
            </div>
            {c.emergencyInfo && <div className="rounded-lg p-2 mt-2 text-[10px] border" style={{ borderColor: '#F59E0B', backgroundColor: '#FFFBEB', color: '#92400E' }}><b>Escalation:</b> {c.emergencyInfo}</div>}
          </>
        );
      }
      case 'dos-donts': {
        const dd = info.dosDonts || {};
        return (
          <>
            {c.introduction && <p className="text-xs mb-3 text-center" style={{ color: cs.text }}>{c.introduction}</p>}
            <div className="grid grid-cols-2 gap-3 flex-1">
              <div className="rounded-lg p-3 border-2" style={{ borderColor: '#059669', backgroundColor: '#ECFDF5' }}>
                <h3 className="text-sm font-bold mb-2 flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-4 h-4" />{dd.dosTitle || 'Do'}</h3>
                {(dd.dos || []).map((d, i) => <div key={i} className="text-xs mb-1.5 text-emerald-900"><b>{d.text}</b>{d.explanation && <div className="text-[10px] opacity-80">{d.explanation}</div>}</div>)}
              </div>
              <div className="rounded-lg p-3 border-2" style={{ borderColor: '#DC2626', backgroundColor: '#FEF2F2' }}>
                <h3 className="text-sm font-bold mb-2 flex items-center gap-1 text-red-700"><XCircle className="w-4 h-4" />{dd.dontsTitle || "Don't"}</h3>
                {(dd.donts || []).map((d, i) => <div key={i} className="text-xs mb-1.5 text-red-900"><b>{d.text}</b>{d.explanation && <div className="text-[10px] opacity-80">{d.explanation}</div>}</div>)}
              </div>
            </div>
          </>
        );
      }
      case 'emergency-protocol': {
        const ep = info.emergencyProtocol || {};
        const codeColors = { red: '#DC2626', blue: '#2563EB', pink: '#DB2777', orange: '#EA580C', gray: '#4B5563', green: '#059669' };
        const cc = codeColors[ep.codeColor] || cs.primary;
        return (
          <>
            <div className="rounded-lg p-2 mb-2 text-white" style={{ backgroundColor: cc }}>
              <div className="text-sm font-bold">{ep.protocolName || c.title}</div>
              {ep.activationCriteria && <div className="text-[10px]">Activate when: {ep.activationCriteria.join('; ')}</div>}
            </div>
            <div className="space-y-1.5 flex-1">
              {(ep.steps || []).map((s, i) => (
                <div key={i} className="flex items-start gap-2 rounded-lg p-2" style={{ backgroundColor: s.critical ? '#FEF2F2' : cs.background }}>
                  <div className="w-6 h-6 rounded flex items-center justify-center text-white text-xs font-bold shrink-0" style={{ backgroundColor: cc }}>{s.number}</div>
                  <div className="flex-1"><div className="text-xs font-bold" style={{ color: cs.text }}>{s.action}{s.timing && <span className="ml-1 text-[9px] font-semibold" style={{ color: cc }}>[{s.timing}]</span>}</div>{s.details && <div className="text-[10px]" style={{ color: cs.text }}>{s.details}</div>}</div>
                </div>
              ))}
            </div>
            {ep.contacts && <div className="mt-2 flex flex-wrap gap-2">{ep.contacts.map((ct, i) => <span key={i} className="text-[10px] font-bold px-2 py-1 rounded" style={{ backgroundColor: cs.background, color: cs.primary }}><Phone className="w-2.5 h-2.5 inline mr-1" />{ct.name}: {ct.number}</span>)}</div>}
          </>
        );
      }
      case 'quick-reference': {
        const qr = info.quickReference || {};
        return (
          <>
            <div className="grid grid-cols-2 gap-2 flex-1">
              {(qr.sections || []).map((s, i) => (
                <div key={i} className="rounded-lg p-2" style={{ backgroundColor: cs.background }}>
                  <h3 className="text-xs font-bold mb-1 flex items-center gap-1" style={{ color: cs.primary }}><Ico name={s.icon} className="w-3.5 h-3.5" />{s.title}</h3>
                  {(s.items || []).map((it, j) => <div key={j} className={`text-[10px] flex justify-between gap-2 ${it.highlight ? 'font-bold' : ''}`} style={{ color: cs.text }}><span>{it.label}</span><span style={{ color: it.highlight ? cs.primary : cs.text }}>{it.value}</span></div>)}
                </div>
              ))}
            </div>
            {qr.mnemonics && qr.mnemonics.length > 0 && <div className="mt-2 rounded-lg p-2" style={{ backgroundColor: cs.secondary }}>{qr.mnemonics.map((m, i) => <div key={i} className="text-[10px]" style={{ color: cs.text }}><b>{m.acronym}</b> — {m.meaning}</div>)}</div>}
          </>
        );
      }
      case 'decision-tree': {
        const dt = info.decisionTree || {};
        const sev = { normal: cs.primary, caution: '#D97706', critical: '#DC2626' };
        const shape = { start: '#059669', question: cs.primary, action: '#2563EB', outcome: '#7C3AED' };
        const nodeById = (id) => (dt.nodes || []).find((n) => n.id === id);
        return (
          <>
            {c.introduction && <p className="text-xs mb-2 italic" style={{ color: cs.text }}>{c.introduction}</p>}
            <div className="flex flex-col items-center gap-1.5 flex-1">
              {(dt.nodes || []).map((n, i) => {
                const outgoing = (dt.branches || []).filter((b) => b.from === n.id);
                return (
                  <React.Fragment key={n.id}>
                    <div className="rounded-lg px-3 py-1.5 text-center text-white text-xs font-semibold max-w-[80%]" style={{ backgroundColor: n.severity ? sev[n.severity] : shape[n.type] || cs.primary, borderRadius: n.type === 'decision' || n.type === 'question' ? 8 : 20 }}>{n.text}</div>
                    {i < (dt.nodes || []).length - 1 && (
                      <div className="flex items-center gap-1 text-[9px]" style={{ color: cs.text }}>
                        <ArrowDown className="w-3 h-3" style={{ color: cs.accent }} />
                        {outgoing.map((b, j) => b.label && <span key={j} className="px-1 rounded font-semibold" style={{ backgroundColor: cs.background, color: cs.primary }}>{b.label}</span>)}
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            {c.keyPoints && <Bullets items={c.keyPoints} title="Key Considerations" />}
          </>
        );
      }
      case 'competency-validation': {
        const cv = info.competencyValidation || {};
        return (
          <>
            <div className="rounded-lg p-2 mb-2 grid grid-cols-2 gap-2 text-[10px]" style={{ backgroundColor: cs.background, color: cs.text }}>
              <div><b>Employee:</b> _______________</div><div><b>Date:</b> ___________</div>
              <div><b>Validity:</b> {cv.validityPeriod || 'Annual'}</div><div><b>Passing:</b> {cv.passingCriteria || 'All required'}</div>
            </div>
            <table className="w-full text-[10px] flex-1" style={{ color: cs.text }}>
              <thead><tr className="text-left" style={{ color: cs.primary }}><th className="pb-1">Skill</th><th className="pb-1 w-12">Req</th><th className="pb-1 w-12">Met</th></tr></thead>
              <tbody>
                {(cv.skills || []).map((s, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: cs.secondary }}><td className="py-1"><b>{s.skill}</b>{s.criteria && <div className="text-[9px] opacity-70">{s.criteria}</div>}</td><td>{s.required ? 'Yes' : 'No'}</td><td><div className="w-3 h-3 border rounded" style={{ borderColor: cs.primary }} /></td></tr>
                ))}
              </tbody>
            </table>
            <div className="mt-2 text-[10px] grid grid-cols-2 gap-2" style={{ color: cs.text }}><div>Evaluator: _______________</div><div>Signature: _______________</div></div>
          </>
        );
      }
      case 'risk-matrix': {
        const rm = info.riskMatrix || {};
        const level = (l, s) => { const v = l * s; if (v >= 15) return '#DC2626'; if (v >= 8) return '#EA580C'; if (v >= 4) return '#D97706'; return '#059669'; };
        return (
          <>
            <div className="space-y-1.5 flex-1">
              {(rm.risks || []).map((r, i) => (
                <div key={i} className="rounded-lg p-2 flex items-start gap-2" style={{ backgroundColor: cs.background }}>
                  <div className="w-10 h-10 rounded flex flex-col items-center justify-center text-white shrink-0" style={{ backgroundColor: level(r.likelihood, r.severity) }}><span className="text-sm font-bold">{r.likelihood * r.severity}</span><span className="text-[7px]">L{r.likelihood}×S{r.severity}</span></div>
                  <div className="flex-1"><div className="text-xs font-bold" style={{ color: cs.text }}>{r.name}</div><div className="text-[10px]" style={{ color: cs.text }}>{r.description}</div>{r.mitigations && <div className="text-[9px]" style={{ color: cs.accent }}>Mitigate: {r.mitigations.join('; ')}</div>}</div>
                </div>
              ))}
            </div>
            {c.keyPoints && <Bullets items={c.keyPoints} title="Notes" />}
          </>
        );
      }
      case 'scenario': {
        const sc = info.scenario || {};
        return (
          <>
            {sc.patientInfo && <div className="rounded-lg p-2 mb-2 text-[11px]" style={{ backgroundColor: cs.background, color: cs.text }}><b style={{ color: cs.primary }}>Patient:</b> {sc.patientInfo}</div>}
            {sc.situation && <p className="text-xs mb-2" style={{ color: cs.text }}><b style={{ color: cs.primary }}>Situation: </b>{sc.situation}</p>}
            {sc.background && <p className="text-[11px] mb-2" style={{ color: cs.text }}><b>Background: </b>{sc.background}</p>}
            <div className="flex-1 space-y-2">
              {(sc.decisionPoints || []).map((dp, i) => (
                <div key={i} className="rounded-lg p-2 border" style={{ borderColor: cs.secondary }}>
                  <div className="text-xs font-bold mb-1" style={{ color: cs.primary }}>{dp.question}</div>
                  {(dp.options || []).map((o, j) => <div key={j} className="text-[10px] flex items-start gap-1" style={{ color: cs.text }}>{o.isCorrect ? <CheckCircle2 className="w-3 h-3 text-emerald-600 mt-0.5" /> : <XCircle className="w-3 h-3 text-red-400 mt-0.5" />}<span>{o.text}</span></div>)}
                </div>
              ))}
            </div>
            {sc.keyLearnings && <Bullets items={sc.keyLearnings} title="Key Learnings" icon={<Lightbulb className="w-4 h-4" />} />}
          </>
        );
      }
      default: // standard, wellness
        return (
          <>
            {c.introduction && <p className="text-xs mb-3" style={{ color: cs.text }}>{c.introduction}</p>}
            {c.keyPoints && <Bullets items={c.keyPoints} title="Key Points" icon={<Star className="w-4 h-4" />} />}
            {c.tips && <Bullets items={c.tips} title="Practical Tips" icon={<Lightbulb className="w-4 h-4" />} />}
            {c.affirmation && <div className="rounded-lg p-3 text-center text-sm font-medium my-2" style={{ backgroundColor: cs.background, color: cs.primary }}>{c.affirmation}</div>}
            {c.resources && c.resources.length > 0 && (
              <div className="mt-2">
                <h3 className="text-sm font-bold mb-1" style={{ color: cs.primary }}>Resources</h3>
                {c.resources.map((r, i) => <div key={i} className="text-[11px]" style={{ color: cs.text }}><b>{r.name}</b>{r.contact && ` — ${r.contact}`}{r.description && <div className="text-[10px] opacity-75">{r.description}</div>}</div>)}
              </div>
            )}
          </>
        );
    }
  };

  return (
    <div className="onepager-preview bg-white p-6 shadow-lg" style={{ fontFamily: 'Inter, sans-serif' }}>
      <Header />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Body />
      </div>
      <Footer />
    </div>
  );
}
